import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { batch, connect } from 'react-redux';
import get from 'lodash/get';
import qs from 'query-string';
import FilterBar from '../component/FilterBar';
import getCampaignWidgets from '../../../actions/getCampaignWidgets';
import { filter } from '../actions/actionTypes';
import preProcessOptions from '../utils/preProcessOptions';
import { normalize, updateFilterValues } from '../utils/queryParams';
import { fetchDataWithAppliedFilters } from '../utils/fetchData';
import { View } from '../../../components/Common/react-native-components';
import { parseQueryParams } from '../utils/queryParams';
import { isRNWebView } from '../../../utils/isRNWebView';
import { debounce } from 'lodash';
import {
  fetchDynamicFilters,
  getSectionFromPath,
  isSameValue,
  reconcileFiltersWithOptions
} from '../utils/filterUtils';
import { INVALIDATE_GENERIC_FULL_DATA } from '../../../reducers/GenericDataReducer';
import { filtersContainerStyling } from './styles';

const FilterBarContainer = ({ dispatch, activeHotelId, filterSlice }) => {
  const location = useLocation();
  const history = useHistory();

  const [localFilters, setLocalFilters] = useState({});
  const [open, setOpen] = useState(false);
  const applied_filters = get(filterSlice, 'applied_filters', {});
  const showLoading = filterSlice.filtersLoading;
  const hasQueryParams = location.search && location.search.length > 0;
  const [batchConfig, setBatchConfig] = useState();
  const isWebView = isRNWebView();
  const styles = filtersContainerStyling(isWebView);

  const currentSection = useMemo(() => {
    return getSectionFromPath(location.pathname);
  }, [location.pathname]);

  const reconcileLocalFiltersWithConfig = config => {
    setLocalFilters(prev => {
      return reconcileFiltersWithOptions(prev, config);
    });
  };

  const handleConfig = (allSectionsConfig, dynamicFilterParams = {}, fetchDynamic = false) => {
    const queryFilters = parseQueryParams(location.search);
    const applied_filter_values = filterSlice.applied_filter_values;

    const hasQueryFilters = queryFilters && Object.keys(queryFilters).length > 0;
    const activeFilters = hasQueryFilters ? queryFilters : {};

    let mergedDefaultFilters = {};
    let mergedFiltersWithLabels = {};

    Object.keys(allSectionsConfig).forEach(section => {
      const filtersConfig = allSectionsConfig[section] || [];
      let defaultFilters = {};

      filtersConfig.forEach(filter => {
        const { key, defaultValue } = filter;

        if (activeFilters[key]) {
          defaultFilters[key] = activeFilters[key];
        } else if (applied_filter_values[key]) {
          defaultFilters[key] = applied_filter_values[key];
        } else if (defaultValue) {
          defaultFilters[key] = [defaultValue];
        }
      });

      mergedDefaultFilters = {
        ...mergedDefaultFilters,
        ...defaultFilters
      };

      mergedFiltersWithLabels = {
        ...mergedFiltersWithLabels,
        ...updateFilterValues(defaultFilters, filtersConfig)
      };
    });

    if (dynamicFilterParams && Object.keys(dynamicFilterParams).length > 0 && fetchDynamic) {
      fetchDynamicFilters({
        dispatch,
        dynamicFilterParams,
        currentTimePeriod: mergedDefaultFilters?.timePeriod,
        currentPropertyId: mergedDefaultFilters?.property_id,
        allSectionsConfig,
        onComplete: (cstFiltersValues, allSectionsConfig) =>
          updateConfigWithFilters(cstFiltersValues, allSectionsConfig, localFilters),
        batchConfig
      });
    }

    dispatch({
      type: filter.SET_APPLIED_FILTER_VALUES,
      payload: { applied_filter_values: mergedDefaultFilters }
    });

    dispatch({
      type: filter.SET_APPLIED_FILTERS,
      payload: { applied_filters: mergedFiltersWithLabels }
    });

    if (Object.keys(localFilters).length === 0) setLocalFilters(mergedFiltersWithLabels);

    dispatch(fetchDataWithAppliedFilters(mergedDefaultFilters, currentSection, allSectionsConfig[currentSection]));
  };

  const updateConfigWithFilters = (cstFiltersValues, allSectionsConfig, latestLocalFilters = {}) => {
    const fullUpdatedConfig = Object.keys(allSectionsConfig).reduce((acc, section) => {
      acc[section] = allSectionsConfig[section].map(filter =>
        cstFiltersValues[filter.key] ? { ...filter, options: cstFiltersValues[filter.key] } : filter
      );
      return acc;
    }, {});
    Object.keys(fullUpdatedConfig).forEach(section => {
      dispatch({
        type: filter.SET_CONFIG,
        payload: { section, config: fullUpdatedConfig[section] }
      });

      if (section === currentSection) {
        reconcileLocalFiltersWithConfig(fullUpdatedConfig[section]);
      }
    });
    dispatch({
      type: filter.SET_DYNAMIC_FILTER_VALUES_LOADING,
      payload: { loading: false }
    });
    if (hasQueryParams && Object.keys(latestLocalFilters).length === 0) {
      handleConfig(fullUpdatedConfig, {}, false);
      dispatch({
        type: filter.SET_FILTER_LOADING,
        payload: { loading: false }
      });
    }
  };

  useEffect(() => {
    if (currentSection === 'default') return;
    const campaign = currentSection;
    const config = get(filterSlice, `config`, null);
    if (config && Object.keys(config).length > 0) {
      handleConfig(config);
      return;
    }
    dispatch({
      type: filter.SET_FILTER_LOADING,
      payload: { loading: true }
    });
    dispatch(
      getCampaignWidgets({
        activeHotelId,
        campaign: 'filter_common',
        pageType: 'filter',
        onSuccess: configResponse => {
          const filtersConfig = get(configResponse, 'widget_list[0].contents.filter_config', {});
          const sections = get(configResponse, 'widget_list[0].contents.sections', []);
          const dynamicFilterParams = get(configResponse, 'widget_list[0].contents.dynamic_filter_params', {});
          const batchConfigFromResponse = get(configResponse, 'widget_list[0].contents.dynamic_filter_batch_config', {
            batchSizeMonths: 6
          });
          setBatchConfig(batchConfigFromResponse);
          dispatch({
            type: filter.SET_DYNAMIC_FILTER_PARAMS,
            payload: { keys: dynamicFilterParams }
          });
          const currentSectionFiltersConfig = get(filtersConfig, currentSection, []);
          currentSectionFiltersConfig.forEach(filter => {
            filter.options = preProcessOptions(filter.options || []);
          });

          const allSectionsConfig = Object.keys(filtersConfig).reduce((acc, section) => {
            acc[section] = (filtersConfig[section] || []).map(filter => ({
              ...filter,
              options: preProcessOptions(filter.options || [])
            }));
            return acc;
          }, {});

          sections.forEach(section => {
            const filtersForSection = get(filtersConfig, section, []);
            filtersForSection.forEach(filter => {
              filter.options = preProcessOptions(filter.options || []);
            });
            dispatch({
              type: filter.SET_CONFIG,
              payload: { section: section, config: filtersForSection }
            });
          });
          if (!hasQueryParams) {
            dispatch({
              type: filter.SET_FILTER_LOADING,
              payload: { loading: false }
            });
          }
          handleConfig(allSectionsConfig, dynamicFilterParams, true);
        },
        onError: error => {
          dispatch({
            type: filter.SET_FILTER_LOADING,
            payload: { loading: false }
          });
          dispatch({
            type: filter.SET_FILTER_ERROR
          });
        }
      })
    );
  }, [currentSection]);

  const handleRun = () => {
    const appliedFilterValues = normalize(localFilters);
    dispatch({ type: INVALIDATE_GENERIC_FULL_DATA });
    dispatch({
      type: filter.SET_APPLIED_FILTER_VALUES,
      payload: { applied_filter_values: appliedFilterValues }
    });
    const stringified = encodeURIComponent(JSON.stringify(appliedFilterValues));
    const parsedQuery = qs.parse(location.search);
    parsedQuery.filters = stringified;

    const newQuery = qs.stringify(parsedQuery);
    history.push(`${location.pathname}?${newQuery}`);

    dispatch({
      type: filter.SET_APPLIED_FILTERS,
      payload: { applied_filters: localFilters }
    });
    dispatch(fetchDataWithAppliedFilters(appliedFilterValues, currentSection, filterSlice?.config[currentSection]));
    setOpen(false);
  };

  const debouncedFetchDynamicFilters = React.useRef(
    debounce((filters, dynamicFilterParams, config) => {
      const timePeriod = filters?.timePeriod?.map(t => t?.fullValue);
      const propertyIds = filters?.property_id?.map(item => item?.fullValue) || [];
      fetchDynamicFilters({
        dispatch,
        dynamicFilterParams,
        currentTimePeriod: timePeriod,
        currentPropertyId: propertyIds,
        allSectionsConfig: config,
        onComplete: (cstFiltersValues, allSectionsConfig) =>
          updateConfigWithFilters(cstFiltersValues, allSectionsConfig, filters),
        batchConfig
      });
    })
  ).current;

  const handleFilterChange = nextFilters => {
    setLocalFilters(nextFilters);
    const timeChanged = !isSameValue(localFilters?.timePeriod, nextFilters?.timePeriod);
    const propertyChanged = !isSameValue(localFilters?.property_id, nextFilters?.property_id);
    if (timeChanged || propertyChanged) {
      debouncedFetchDynamicFilters(nextFilters, filterSlice.dynamicFilterParams, filterSlice.config, batchConfig);
    }
  };

  if (currentSection === 'default') {
    return <></>;
  }
  
  return (
    <View style={!open || isWebView ? styles.container : {}}>
      <FilterBar
        key={JSON.stringify(filterSlice.config?.[currentSection]?.map(f => f.options?.length))}
        filters={localFilters}
        appliedFilters={applied_filters}
        onFiltersChange={handleFilterChange}
        onRun={handleRun}
        showLoading={showLoading}
        config={get(filterSlice, `config.${currentSection}`, [])}
        dynamicFilterLoading={filterSlice?.dynamicFilterValuesLoading}
        open={open}
        setOpen={setOpen}
      />
    </View>
  );
};

const mapStateToProps = state => ({
  activeHotelId: state.app?.active_hotel_id,
  language: state.app?.language,
  allMessageFetched: state.app?.allMessageFetched,
  filterSlice: state.filterReducer
});

export default connect(mapStateToProps)(FilterBarContainer);
